import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

export class Utils {
  /**
   * Get the first and last day of the current week.
   * @returns {Object} An object with `firstDay` and `lastDay` properties representing the first and last day of the week in "yyyy/mm/dd" format.
   */
  static getFirstAndLastDayOfWeek(): { startDate: string; finalDate: string } {
    const currentDate = new Date();
    const currentDay = currentDate.getDay(); // Get the current day of the week (0 - Sunday, 1 - Monday, ...)
    const startDayOfWeek = new Date(currentDate); // Create a copy of the current date

    // Calculate the offset to find the start of the week (Sunday)
    const offset = currentDay === 0 ? 6 : currentDay; // Calculate the number of days to subtract
    startDayOfWeek.setDate(currentDate.getDate() - offset);

    // Calculate the end of the week (Saturday)
    const endDayOfWeek = new Date(startDayOfWeek);
    endDayOfWeek.setDate(startDayOfWeek.getDate() + 6);

    // Format the dates as "yyyy/mm/dd" strings
    const startDate = this.formatDate(startDayOfWeek);
    const finalDate = this.formatDate(endDayOfWeek);

    return { startDate, finalDate };
  }

  /**
   * Returns the interval of the previous week, starting from 7 days ago and ending on the current day.
   *
   * @returns {Object} An object containing the start and end dates of the week in "yyyy/mm/dd" format.
   */
  static getPreviousWeekInterval(): { startDate: string; finalDate: string } {
    const currentDate = new Date();
    const startDayOfWeek = new Date(currentDate); // Create a copy of the current date
    startDayOfWeek.setDate(currentDate.getDate() - 7); // Subtract 6 days to get the start of the week

    // Format the dates as "yyyy/mm/dd" strings
    const startDate = this.formatDate(startDayOfWeek);
    const finalDate = this.formatDate(currentDate);

    return { startDate, finalDate };
  }

  /**
   * Format a Date object as a string in "yyyy/mm/dd" format.
   * @param {Date} date - The Date object to be formatted.
   * @returns {string} The formatted date string.
   */
  private static formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format a date in the format "MM/DD/YYYY".
   *
   * @param {any} dateObj - The date object to be formatted.
   * @returns {string} The formatted date string.
   */
  static formatDateUs(dateObj: any) {
    const day = dateObj.date();
    const month = dateObj.month() + 1;
    const year = dateObj.year();

    // Formate a data no formato "MM/DD/YYYY"
    const formattedDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;

    return formattedDate;
  }

  /**
   * Formats a date from "yyyy-MM-dd" format to "MMM d, yyyy" format.
   * @param {string} date - The date in "yyyy-MM-dd" format.
   * @returns {string} The formatted date in "MMM d, yyyy" format.
   */
  static formatDateToUsName(date: string): string {
    // Convert the date to UTC before creating the Date object
    const dateUTC = date + 'T00:00:00Z';

    // Create the Date object from the UTC date
    const dateObj = new Date(dateUTC);

    // Create a formatter using the UTC timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });

    // Format the date using the formatter.
    const formattedDate = formatter?.format(dateObj);

    // Return the formatted date.
    return formattedDate;
  }

  /**
   * Formats a date according to the specified format string.
   *
   * @param {Date} date - The date to be formatted.
   * @param {string} formatString - The desired format string. Refer to the date-fns documentation for valid format options.
   * @returns {string} The formatted date.
   */
  static formatDateFns(date: any, formatString: any) {
    return format(date, formatString);
  }

  static filterRequiredByLink(menuItems: any[], link: string): string[] | null {
    // Verifique se o link está vazio
    if (!link.trim()) return null;

    // Percorra todos os itens do menu
    for (const menuItem of menuItems) {
      // Se o link corresponder ao link do item do menu
      if (menuItem.link === link) {
        // Retorna a lista de required desse item
        return menuItem.required;
      }

      // Se houver submenus, verifique neles também
      if (menuItem.menuShortcuts) {
        const required = this.filterRequiredByLink(menuItem.menuShortcuts, link);
        if (required) return required;
      }

      if (menuItem.areas) {
        const required = this.filterRequiredByLink(menuItem.areas, link);
        if (required) return required;
      }

      if (menuItem.child) {
        const required = this.filterRequiredByLink(menuItem.child, link);
        if (required) return required;
      }
    }

    // Se nenhum link correspondente for encontrado, retorne null
    return null;
  }

  /**
   * Converts a string to camelCase format.
   * @param {string} text - The text to convert to camelCase.
   * @returns {string} The text in camelCase format.
   * @example
   * Utils.toCamelCase('Hello World') // returns 'helloWorld'
   * Utils.toCamelCase('PROJECT MANAGEMENT') // returns 'projectManagement'
   */
  static toCamelCase(text: string): string {
    return text
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  }
}

// JSDoc Tags Explanation:
// - @returns: Used to describe the type of return value of a function or method.
// - @param: Used to describe a parameter of a function or method.
// - @throws or @exception: Used to describe exceptions that a function or method can throw.
// - @description or @desc: Used to provide a detailed description of a function or method.
// - @example: Used to provide examples of how to use a function or method.
// - @see: Used to provide references to related resources or documentation.
// - @deprecated: Used to indicate that a function or method is deprecated and should not be used.
// - @inheritdoc: Used to inherit the documentation from a parent class or interface.
// - @private: Used to indicate that a function, method, or member is private and should not be accessed externally.
// - @public: Used to indicate that a function, method, or member is public and can be accessed externally.
// - @protected: Used to indicate that a function, method, or member is protected and can be accessed by derived classes.
// - @readonly: Used to indicate that a property is read-only and cannot be modified.
// - @typedef: Used to define a custom type or alias for a type.
// - @enum: Used to define an enumeration type.
// - @callback: Used to define a callback function type.

// These are some of the commonly used JSDoc tags, but there are many more available. You can explore the JSDoc documentation for a complete list of tags and their usage.
